/* eslint-disable max-lines-per-function */

import {
  ApplicationConfig,
  Injector,
  LOCALE_ID,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { provideRouter, TitleStrategy } from '@angular/router';
import { routes } from './app.routes';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { MatPaginatorIntl } from '@angular/material/paginator';

import {
  API_BASE_URL_TOKEN,
  AUTH_CONFIG_TOKEN,
  ActionGateAdapter,
  tokenInterceptor,
  AuthService,
  errorInterceptor,
  getEnvProfile,
  SpanishPaginatorIntl,
  NebulaTitleStrategy,
  TranslationService,
  BREADCRUMB_SYNC_SERVICE,
  CUSTOM_ICONS_TOKEN,
  DEFAULT_CUSTOM_ICONS,
} from '@nebula-shared/data-access';

import { BreadcrumbSyncService } from '@nebula-layout/layout';

import {
  NEBULA_ACTION_GATE,
  NEBULA_UI_CONFIG,
  NebulaNavigationService,
  provideNebulaCoreIcons,
  provideNebulaUIConfig,
} from 'nebula-ui-kit';
import {
  Activity,
  ArchiveRestore,
  Globe,
  Map as MapIcon,
  MapPin,
  ArrowLeft,
  ArrowLeftRight,
  ArrowRight,
  Ban,
  BarChart3,
  BetweenHorizontalEnd,
  BookOpen,
  BookText,
  Briefcase,
  Building,
  Building2,
  Calendar,
  CalendarRange,
  CircleCheck,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  CircleArrowLeft,
  CircleDollarSign,
  Cloud,
  Ellipsis,
  File,
  FileClock,
  FileDown,
  FilePlus,
  FileSliders,
  FileSpreadsheet,
  FileUp,
  FileUser,
  FileLock,
  FileX,
  Folder,
  FolderArchive,
  FolderGit2,
  FolderKanban,
  FolderLock,
  FolderSync,
  FolderUp,
  Gauge,
  HardDrive,
  History,
  House,
  Inbox,
  Key,
  LayoutTemplate,
  Loader,
  Loader2,
  ListChecks,
  Logs,
  LUCIDE_ICONS,
  LucideIconProvider,
  MessageSquareDot,
  PlusCircle,
  RefreshCw,
  Save,
  SearchCheck,
  Server,
  Shield,
  Sliders,
  SlidersHorizontal,
  SquareKanban,
  SquarePen,
  Table,
  Tags,
  UserKey,
  UserPlus,
  UserCheck,
  UserX,
  Trash2,
  Users,
  UserStar,
  Vault,
  Zap,
  CheckCircle,
  ClockFading,
  Pen,
  ClipboardClock,
  CalendarClock,
} from 'lucide-angular';
import localeEsCo from '@angular/common/locales/es-CO';
import { registerLocaleData } from '@angular/common';

registerLocaleData(localeEsCo);

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes),
    provideHttpClient(withInterceptors([tokenInterceptor, errorInterceptor])),
    provideNebulaUIConfig({
      confirmOkText: 'Aceptar',
      confirmCancelText: 'Cancelar',
    }),
    ...provideNebulaCoreIcons(),
    NebulaNavigationService,

    provideAppInitializer(() => inject(TranslationService).init()),

    {
      provide: BREADCRUMB_SYNC_SERVICE,
      useFactory: () => inject(BreadcrumbSyncService),
    },
    {
      provide: CUSTOM_ICONS_TOKEN,
      useValue: DEFAULT_CUSTOM_ICONS,
    },

    {
      provide: NEBULA_UI_CONFIG,
      useFactory: () => {
        const injector = inject(Injector);
        return {
          navStorageKey: 'nebula_ui_nav_v1',
          searchPlaceholder: 'Buscar',
          getSessionCurrency: () => injector.get(AuthService).getSessionCurrency(),
        };
      },
    },
    {
      provide: LUCIDE_ICONS,
      useFactory: () =>
        new LucideIconProvider({
          Activity,
          Zap,
          ArchiveRestore,
          Globe,
          Map: MapIcon,
          MapPin,
          ArrowLeft,
          ArrowLeftRight,
          ArrowRight,
          Ban,
          BarChart3,
          BetweenHorizontalEnd,
          BookOpen,
          Briefcase,
          Building,
          Building2,
          Calendar,
          CalendarRange,
          CircleCheck,
          ChevronLeft,
          ChevronRight,
          ChevronDown,
          CircleArrowLeft,
          CircleDollarSign,
          Cloud,
          Ellipsis,
          File,
          FilePlus,
          FileLock,
          FileSliders,
          FileUser,
          Folder,
          FolderArchive,
          FolderGit2,
          FolderKanban,
          FolderLock,
          FolderSync,
          FolderUp,
          Gauge,
          HardDrive,
          History,
          House,
          Inbox,
          Key,
          LayoutTemplate,
          Loader,
          Loader2,
          ListChecks,
          MessageSquareDot,
          PlusCircle,
          RefreshCw,
          Save,
          SearchCheck,
          Server,
          Shield,
          Sliders,
          SlidersHorizontal,
          SquareKanban,
          SquarePen,
          Tags,
          Table,
          UserKey,
          UserPlus,
          UserCheck,
          UserX,
          Trash2,
          Users,
          UserStar,
          Vault,
          Logs,
          FileClock,
          FileSpreadsheet,
          FileDown,
          FileUp,
          BookText,
          FileX,
          CheckCircle,
          ClockFading,
          Pen,
          ClipboardClock,
          CalendarClock,
        }),
      multi: true,
    },

    {
      provide: LOCALE_ID,
      useValue: 'es-CO',
    },

    {
      provide: MatPaginatorIntl,
      useClass: SpanishPaginatorIntl,
    },
    {
      // El titulo de la pestaña del navegador sale del bundle: sin esto Angular
      // aplica el `title` de la ruta tal cual, y una ruta que lo declara como
      // clave termina mostrando el identificador crudo.
      provide: TitleStrategy,
      useClass: NebulaTitleStrategy,
    },

    {
      provide: API_BASE_URL_TOKEN,
      useValue: getEnvProfile().apiBaseUrl,
    },

    {
      provide: AUTH_CONFIG_TOKEN,
      useValue: {
        baseUrl: getEnvProfile().apiBaseUrl,
      },
    },

    {
      provide: NEBULA_ACTION_GATE,
      useExisting: ActionGateAdapter,
    },
  ],
};
